import type { NamedExoticComponent, RefAttributes } from 'react';
import type React from 'react';
import type { AspectRatio, VideoData } from '@lumen5/luminary';

import type {
    AIVoice,
    AssetContainerModel,
    AssetTypes,
    Blueprint,
    DashboardVideo,
    Pronunciation,
    SlideBackgroundModel,
    Template,
} from 'js/src/client';
import type { TrimmerSource } from 'js/src/components/common/asset-trimmer/common';
import type { ButtonType } from 'js/src/components/common/ui/Button';
import type { MODAL_SIZES } from 'js/src/components/common/ui/modal';
import type { SearchContextValueType } from 'js/src/data/media';
import type { PronunciationIDType } from 'js/src/data/pronunciations/pronunciations-types';
import type { UploadAssetContainerTypes } from 'js/src/data/uploads';
import type { ImmutableMap } from 'js/src/libs/consts';
import type { SfxType } from 'js/src/types';

/* ACTION TYPES */
export enum MODALS_ACTIONS {
    SHOW_MODAL = 'SHOW_MODAL',
    HIDE_MODAL = 'HIDE_MODAL',
}

export enum MODAL_IDS {
    GENERIC_ERROR = 'GENERIC_ERROR',
    FEATURE_LOCK = 'FEATURE_LOCK',
    VIDEO_PREVIEW = 'VIDEO_PREVIEW',
    UPLOAD_MEDIA = 'UPLOAD_MEDIA',
    PROJECT_CREATE = 'PROJECT_CREATE',
    VIDEO_LIST_MEMBERS_UPDATE = 'VIDEO_LIST_MEMBERS_UPDATE',
    PROJECT_DELETE = 'PROJECT_DELETE',
    TEAM_MEMBER_INVITATION_MODAL = 'TEAM_MEMBER_INVITATION_MODAL',
    LOGIN_REQUIRED_FOR_COMMENTING = 'LOGIN_REQUIRED_FOR_COMMENTING',
    CROP = 'CROP',
    TRIM = 'TRIM',
    INSERT_TRIM = 'INSERT_TRIM',
    APPROVAL_STATUS = 'APPROVAL_STATUS',
    CREATE_BLUEPRINT_FROM_VIDEO = 'CREATE_BLUEPRINT_FROM_VIDEO',
    TRANSCRIPT_GENERATED = 'TRANSCRIPT_GENERATED',
    REMOVE_TEAM_MEMBER = 'REMOVE_TEAM_MEMBER',
    USER_SETTINGS = 'USER_SETTINGS',
    RECORD_YOURSELF = 'RECORD_YOURSELF',
    RECORD_YOUR_SCREEN = 'RECORD_YOUR_SCREEN',
    RECORD_VIDEO_SELECTION = 'RECORD_VIDEO_SELECTION',
    VOICEDRIVEN_SCENE_CREATION = 'VOICEDRIVEN_SCENE_CREATION',
    AI_VOICE_SELECTION = 'AI_VOICE_SELECTION',
    AI_AVATAR_VOICE_PRESET_SELECTION = 'AI_AVATAR_VOICE_PRESET_SELECTION',
    EDIT_PRONUNCIATION = 'EDIT_PRONUNCIATION',
    DELETE_PRONUNCIATION = 'DELETE_PRONUNCIATION',
    MEDIA_DETAILS = 'MEDIA_DETAILS',
    GENERATIVE_MEDIA_DETAILS = 'GENERATIVE_MEDIA_DETAILS',
    AUDIO_VISUALIZATION = 'AUDIO_VISUALIZATION',
    TEMPLATE_PICKER = 'TEMPLATE_PICKER',
    SKETCH_BLUEPRINTS = 'SKETCH_BLUEPRINTS',
    SUBMIT_FEEDBACK = 'SUBMIT_FEEDBACK',
    CLIP_PREVIEW = 'CLIP_PREVIEW',
    VIDEO_NAME_EDITING = 'VIDEO_NAME_EDITING',
    PREMIUM_MEDIA_UPGRADE = 'PREMIUM_MEDIA_UPGRADE',
    CREATE_BLUEPRINT = 'CREATE_BLUEPRINT',
    RENAME_FOLDER = 'RENAME_FOLDER',
    DELETE_FOLDER = 'DELETE_FOLDER',
    CREATE_FOLDER = 'CREATE_FOLDER',
    UPLOAD_STICKERS = 'UPLOAD_STICKERS',
    FEATURE_FLAGS_MANAGEMENT = 'FEATURE_FLAGS_MANAGEMENT',
    SOUND_EFFECT_SELECTION = 'SOUND_EFFECT_SELECTION',
    VOICE_CLONING = 'VOICE_CLONING',
    VOICE_EDIT = 'VOICE_EDIT',
    USE_CASE_PREVIEW = 'USE_CASE_PREVIEW',
    NARRATION_STYLE = 'NARRATION_STYLE',
    RETURN_TO_ANIMATION_EDITOR = 'RETURN_TO_ANIMATION_EDITOR',
}

export enum LOCKED_FEATURES {
    RENDER_720P = 'RENDER_720P',
    RENDER_1080P = 'RENDER_1080P',
    UNLIMITED_PREMIUM_MEDIA = 'UNLIMITED_PREMIUM_MEDIA',
    MARKETPLACE_MEDIA = 'MARKETPLACE_MEDIA',
    HIT_MEDIA_CREDIT_LIMIT = 'HIT_MEDIA_CREDIT_LIMIT',
    HIT_IMAGE_TRANSFORM_MEDIA_CREDIT_LIMIT = 'HIT_IMAGE_TRANSFORM_MEDIA_CREDIT_LIMIT',
    CUSTOM_WATERMARK = 'CUSTOM_WATERMARK',
    CUSTOM_FONT = 'CUSTOM_FONT',
    MULTIPLE_WORKSPACES = 'MULTIPLE_WORKSPACES',
    HIT_MAX_WORKSPACES = 'HIT_MAX_WORKSPACES',
    BRAND_KITS_LIMIT = 'BRAND_KITS_LIMIT',
    BRAND_KITS_LIMIT_CONTACT_US = 'BRAND_KITS_LIMIT_CONTACT_US',
    TRANSLATION = 'TRANSLATION',
    GENERATE_SCRIPT_CONTROLS = 'GENERATE_SCRIPT_CONTROLS',
    AI_VOICEOVER_MINUTES_LIMIT = 'AI_VOICEOVER_MINUTES_LIMIT',
    MAX_VIDEO_DURATION_LIMIT = 'MAX_VIDEO_DURATION_LIMIT',
    TEAM_MEMBERS = 'TEAM_MEMBERS',
    TEAM_MEMBERS_ENTERPRISE = 'TEAM_MEMBERS_ENTERPRISE',
    TEAM_MEMBERS_LIMIT = 'TEAM_MEMBERS_LIMIT',
    VIDEO_LIMIT = 'VIDEO_LIMIT',
    CANT_PUBLISH_AI_AVATAR = 'CANT_PUBLISH_AI_AVATAR',
    AI_AVATAR_SCRIPT_COMPOSER = 'AI_AVATAR_SCRIPT_COMPOSER',
    AUDIO_ENHANCEMENT = 'AUDIO_ENHANCEMENT',
    IMAGE_BACKGROUND_REMOVAL = 'IMAGE_BACKGROUND_REMOVAL',
    AI_IMAGE_GENERATION_MODELS = 'AI_IMAGE_GENERATION_MODELS',
    VOICE_CLONING_LIMIT = 'VOICE_CLONING_LIMIT',
    ANIMATION_MINUTES = 'ANIMATION_MINUTES',
    GENX_SCRIPT_DURATION = 'GENX_SCRIPT_DURATION',
    FREE_USER_GENX_IMAGE_LIMIT = 'FREE_USER_GENX_IMAGE_LIMIT',
    PAID_GENX_IMAGE_LIMIT = 'PAID_GENX_IMAGE_LIMIT',
}

export type FeatureLockModalContext = {
    featureName: LOCKED_FEATURES;
    onClose?: () => void;
    subCategory?: string;
    secondaryCtaText?: string;
    onClickSecondaryCta?: VoidFunction;
    premiumMediaCreditsLeft?: number;
};

export type UploadModalContext = {
    uploadSessionId: string;
    assetContainerTypes: UploadAssetContainerTypes;
    constrainToBackgroundAssetType?: AssetTypes.VIDEO | AssetTypes.IMAGE;
    constrainToSingleFile?: boolean;
    uploadConfirmedCallback?: (uploadPromise: Promise<string[]>) => void;
};

export type TranscriptGeneratedModalContext = {
    sceneGroupId: string;
};

export type EditPronunciationModalContext = {
    voicePreset: string;
    targetWord: Pronunciation['target_word'];
    languageCode: Pronunciation['language'];
    onApply?: (updatedPronunciation: Pronunciation['pronunciation']) => void;
    showLinkToLibrary?: boolean;
    existingWordLevelPronunciation?: string;
    allowWordLevelOverride?: boolean;
    onApplyWordLevel?: (updatedPronunciation: Pronunciation['pronunciation']) => void;
    previewContextBefore?: string;
    previewContextAfter?: string;
};

export type MediaDetailsModalContext = {
    media: AssetContainerModel;
};

export type GenerativeMediaDetailsModalContext = {
    media: SlideBackgroundModel;
    isMediaSaved: boolean;
};

export type GenericErrorButtonConfig = {
    component: NamedExoticComponent<
        ButtonType & RefAttributes<HTMLAnchorElement | HTMLButtonElement>
    >;
    text: string;
    onClick?: () => void;
};

export type GenericErrorModalContext = {
    title: string;
    text?: string;
    content?: React.ReactNode;
    size?: MODAL_SIZES;
    buttons?: GenericErrorButtonConfig[];
    closeable?: boolean;
    onClose?: () => void;
};

export type VoicedrivenSceneCreationModalContext = {
    sceneGroupCategory:
        | typeof VideoData.CATEGORY_TALKING_HEAD
        | typeof VideoData.CATEGORY_VOICEOVER;
    // Provided when a scene is already created when the modal is opened (e.g. coming from new video flow)
    sceneGroupId?: string;
    // When sceneGroupId is not provided (e.g. coming from the supermenu)
    // an index must be provided to indicate where to create and place the scene group
    targetSceneGroupIndex?: number;
};

export type CropModalContext = {
    sceneId: string;
    sceneGroupId: string;
    assetId: string;
    // To indicate if the crop modal is opened from the media toolbar within the scene vs the one in the SceneGroupRowHeader
    fromScene?: boolean;
};

export type TrimModalContext = {
    sceneId: string;
    sceneGroupId: string;
    assetId: string;
    trimmerSource: TrimmerSource;
};

export type InsertTrimModalContext = {
    sceneGroupId: string;
    trimmerSource: TrimmerSource;
};

export type UserDropdownModalContext = {
    teamMemberId: number;
};

export enum VOICE_SELECTION_MODAL_SOURCE {
    SCRIPT_EDIT_PAGE = 'Script Edit Page',
    TRANSCRIPT_TITLE = 'Transcript Title',
    ANIMATION_EDITOR = 'Animation Editor',
}

export type VoiceSelectionModalContext = {
    source: VOICE_SELECTION_MODAL_SOURCE;
    initialSelectedVoiceId: string | null;
    onApply: (voice: AIVoice, newVoiceSpeed: number | null) => void;
    initialVoiceSpeed: number | null;
    onRemoveVoiceover?: () => void;
    onBack?: () => void;
};

export enum AVATAR_VOICE_PRESET_SELECTION_MODAL_SOURCE {
    SCRIPT_EDIT_PAGE = 'Script Edit Page',
    TRANSCRIPT_TITLE = 'Transcript Title',
}

export type AvatarVoicePresetSelectionModalContext = {
    source: AVATAR_VOICE_PRESET_SELECTION_MODAL_SOURCE;
    initialSelectedPresetId: number | null;
    onApply: (presetId: number) => void;
};

export type RecordYourselfModalContext = {
    isVideoRecording: boolean;
    isScreenRecording: boolean;
    forceSceneOverwrite?: boolean;
    uploadConfirmedCallback?: (uploadPromise: Promise<string[]>) => void;
};

export type RecordYourScreenModalContext = {
    uploadConfirmedCallback?: (uploadPromise: Promise<string[]>) => void;
};

export type DeletePronunciationModalContext = {
    pronunciationId: PronunciationIDType;
};

export type CreateSimilarVideoModalContext = {
    videoId: number;
    projectId: number | null;
    videoName: string;
    videoAspectRatio: AspectRatio;
};

export type CreateBlueprintFromVideoModalContext = {
    videoName: string;
    onCancelOrCreation?: VoidFunction;
    isSimilarVideo?: boolean;
};

export type CreateBlueprintFromVideoDashboardModalContext = {
    video: DashboardVideo;
    templateDisplayName: string;
    onBlueprintCreated?: VoidFunction;
};

export type ClipPreviewModalContext = {
    clipId: number;
};

export type VideoNameEditingModalContext = {
    videoId: number;
    currentVideoName: string;
};

export type TemplatePickerModalContext = {
    onSelect: (template: ImmutableMap<Template>) => void;
};

export type SketchBlueprintsModalContext = {
    onSelect: (blueprint: Blueprint) => void;
};

export type CreateBlueprintModalContext = {
    videos?: DashboardVideo[];
};

export type RenameFolderModalContext = {
    folderId: number;
    folderName: string;
};

export type DeleteFolderModalContext = {
    folderId: number;
    count: number;
};

export type CreateFolderModalContext = {
    searchContext: SearchContextValueType;
};

export type SoundEffectSelectionModalContext = {
    assetId: string;
    sceneId: string;
    type: SfxType;
};

export type VoiceCloningModalContext = {
    returnToVoiceSelection?: VoiceSelectionModalContext;
    gcsUrl?: string;
};

export type VoiceEditModalContext = {
    voiceSlug: string;
    returnToVoiceSelection?: VoiceSelectionModalContext;
};

export type UseCasePreviewModalContext = {
    blueprintId: number;
};

export type NarrationStyle = 'voiceover' | 'text' | 'avatar';

export type NarrationStyleModalContext = {
    narrationStyle: NarrationStyle;
};
